import React, { useEffect, useState } from 'react';
import { adminGetSubscribers, adminSendCampaign, adminGetProducts, adminGetMembers, adminGetEmailLogs, adminMemberResetLink, adminMemberCredit, adminMemberSetPassword, adminMemberUpdate, adminMemberDelete } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Box } from './AdminUI';
import { useToast } from '../../hooks/use-toast';
import { Mail, Send, Users, Sparkles, History, CheckCircle2 } from 'lucide-react';
import { adminAiGenerate } from '../../lib/api';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '\u2014');

export default function CampaignPanel() {
  const { toast } = useToast();
  const [subs, setSubs] = useState({ total: 0, newStockCount: 0, specialsCount: 0 });
  const [products, setProducts] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [history, setHistory] = useState([]);
  const [audience, setAudience] = useState('specials');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [featured, setFeatured] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const AUDIENCE_LABELS = { specials: 'Specials & Sales', newStock: 'New Stock', members: 'Exclusive members-only send' };

  const writeWithClaude = async () => {
    setAiBusy(true);
    try {
      const featuredNames = products.filter((p) => featured.includes(p.id)).map((p) => `${p.name} ${p.size || ''}`.trim());
      const brief = subject.trim() || message.trim() || AUDIENCE_LABELS[audience];
      const context = `Campaign type: ${AUDIENCE_LABELS[audience]}.` + (featuredNames.length ? ` Featured products: ${featuredNames.join(', ')}.` : '');
      const res = await adminAiGenerate('campaign', brief, '', context);
      if (res.subject) setSubject(res.subject);
      if (res.message) setMessage(res.message);
      toast({ title: 'Claude drafted your blast', description: 'Review and tweak before sending.' });
    } catch (e) { toast({ title: 'Claude could not write this', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setAiBusy(false); }
  };

  const loadHistory = () => adminGetEmailLogs().then((logs) => setHistory((logs || []).filter((l) => l.type === 'campaign'))).catch(() => {});

  useEffect(() => {
    adminGetSubscribers().then(setSubs).catch(() => {});
    adminGetProducts().then((p) => setProducts(p.filter((x) => !x.hidden))).catch(() => {});
    adminGetMembers().then((d) => setMembers(d.members || [])).catch(() => {});
    loadHistory();
  }, []);

  const toggleFeatured = (id) => setFeatured((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleMember = (email) => setSelected((s) => ({ ...s, [email]: !s[email] }));
  const selectedEmails = members.map((m) => m.email).filter((e) => selected[e]);

  const copyResetLink = async (m) => {
    try {
      const res = await adminMemberResetLink(m.id);
      try { await navigator.clipboard.writeText(res.resetUrl); } catch (e) {}
      toast({ title: 'Reset link copied', description: `Send it to ${m.email} via any channel. Single-use, valid ${res.expiresHours}h.` });
      window.prompt('Reset link (copied to clipboard):', res.resetUrl);
    } catch (e) { toast({ title: 'Could not create reset link', variant: 'destructive' }); }
  };

  const adjustCredit = async (m) => {
    const raw = window.prompt(`Adjust store credit for ${m.fullName || m.email}\nCurrent: $${Number(m.credit || 0).toFixed(2)}\nEnter amount (e.g. 25 to add, -10 to remove):`);
    if (raw === null || !raw.trim()) return;
    const amount = parseFloat(raw);
    if (!amount || Number.isNaN(amount)) { toast({ title: 'Enter a valid non-zero amount', variant: 'destructive' }); return; }
    const note = window.prompt('Note (optional):') || '';
    try {
      const res = await adminMemberCredit(m.id, amount, note);
      setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, credit: res.credit } : x)));
      toast({ title: `Credit ${res.applied >= 0 ? 'added' : 'removed'}`, description: `${m.email} now has $${res.credit.toFixed(2)} store credit.` });
    } catch (e) { toast({ title: 'Credit update failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const setMemberPassword = async (m) => {
    const pw = window.prompt(`Set a new password for ${m.email}\n(min 6 characters - share it with the customer securely):`);
    if (pw === null) return;
    if ((pw || '').length < 6) { toast({ title: 'Password must be at least 6 characters', variant: 'destructive' }); return; }
    try {
      await adminMemberSetPassword(m.id, pw);
      toast({ title: 'Password updated', description: `${m.email} can now sign in with the new password.` });
    } catch (e) { toast({ title: 'Failed to set password', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const editMember = async (m) => {
    const name = window.prompt('Full name:', m.fullName || '');
    if (name === null) return;
    const email = window.prompt('Email:', m.email || '');
    if (email === null) return;
    const phone = window.prompt('Phone (optional):', m.phone || '');
    if (phone === null) return;
    try {
      await adminMemberUpdate(m.id, { fullName: name, email, phone });
      setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, fullName: name.trim() || x.fullName, email: (email.trim() || x.email).toLowerCase(), phone: phone.trim() } : x)));
      toast({ title: 'Member updated' });
    } catch (e) { toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
  };

  const deleteMember = async (m) => {
    if (!window.confirm(`Delete the account for ${m.email}?\nTheir order history stays, but they will no longer be able to sign in. This cannot be undone.`)) return;
    try {
      await adminMemberDelete(m.id);
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
      toast({ title: 'Account deleted', description: m.email });
    } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const filteredMembers = memberSearch.trim()
    ? members.filter((m) => `${m.fullName} ${m.email}`.toLowerCase().includes(memberSearch.trim().toLowerCase()))
    : members;
  const allFilteredSelected = filteredMembers.length > 0 && filteredMembers.every((m) => selected[m.email]);
  const toggleAllFiltered = () => {
    const next = { ...selected };
    filteredMembers.forEach((m) => { next[m.email] = !allFilteredSelected; });
    setSelected(next);
  };

  const send = async (isTest) => {
    if (!subject.trim()) { toast({ title: 'Add a subject', variant: 'destructive' }); return; }
    if (isTest && !testEmail.trim()) { toast({ title: 'Enter a test email', variant: 'destructive' }); return; }
    if (!isTest && audience === 'members' && selectedEmails.length === 0) {
      toast({ title: 'Select at least one member', description: 'Tick members in the list below first.', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const res = await adminSendCampaign({
        audience, subject, message, productIds: featured,
        testEmail: isTest ? testEmail.trim() : null,
        emails: audience === 'members' ? selectedEmails : null,
      });
      toast({
        title: isTest ? 'Test sent' : 'Campaign sent',
        description: `${res.recipients} recipient(s) \u00b7 ${res.sent} sent, ${res.logged} logged${res.logged && !res.sent ? ' (SMTP not active in preview)' : ''}.`,
      });
      if (!isTest) loadHistory();
    } catch (e) { toast({ title: 'Failed to send', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setSending(false); }
  };

  const audienceCount = audience === 'newStock' ? subs.newStockCount : audience === 'members' ? selectedEmails.length : subs.specialsCount;
  const audienceLabel = audience === 'members' ? 'selected member(s)' : 'opted-in customer(s)';

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Composer */}
      <div className="border border-alp-line rounded-lg p-6 bg-alp-card/40" data-testid="campaign-panel">
        <h3 className="font-serif text-xl text-alp-cream mb-1 flex items-center gap-2"><Mail size={18} /> Email Campaigns</h3>
        <p className="text-alp-gray text-sm mb-4">Send new-stock or specials emails to opted-in customers, or hand-pick registered members below.</p>

        <div className="flex flex-wrap gap-3 mb-6">
          <span className="inline-flex items-center gap-2 text-xs text-alp-cream border border-alp-line rounded px-3 py-1.5" data-testid="subs-total"><Users size={13} className="text-alp-gold" /> {subs.total} subscribers</span>
          <span className="inline-flex items-center gap-2 text-xs text-alp-cream border border-alp-line rounded px-3 py-1.5">New stock: {subs.newStockCount}</span>
          <span className="inline-flex items-center gap-2 text-xs text-alp-cream border border-alp-line rounded px-3 py-1.5">Specials: {subs.specialsCount}</span>
          <span className="inline-flex items-center gap-2 text-xs text-alp-cream border border-alp-gold/40 rounded px-3 py-1.5" data-testid="members-total-chip">Members: {members.length}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Box label="Campaign type">
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="campaign-audience" className="bg-alp-dark border-alp-line text-alp-cream"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="specials">Specials &amp; Sales</SelectItem>
                <SelectItem value="newStock">New Stock</SelectItem>
                <SelectItem value="members">Selected Members (pick below)</SelectItem>
              </SelectContent>
            </Select>
          </Box>
          <Box label="Sending to"><div className="h-10 flex items-center text-sm text-alp-gold" data-testid="campaign-recipient-count">{audienceCount} {audienceLabel}</div></Box>
        </div>

        <button onClick={writeWithClaude} disabled={aiBusy} data-testid="ai-gen-campaign-btn" className="w-full mt-2 mb-1 inline-flex items-center justify-center gap-2 border border-alp-gold/50 text-alp-gold px-4 py-2.5 text-xs tracking-[0.15em] uppercase rounded hover:bg-alp-gold hover:text-alp-black transition-colors disabled:opacity-50">
          <Sparkles size={14} className={aiBusy ? 'animate-pulse' : ''} /> {aiBusy ? 'Claude is writing…' : 'Write it with Claude'}
        </button>
        <p className="text-alp-gray text-[11px] mb-1">Tip: type a rough idea in Subject (e.g. &ldquo;weekend 15% off&rdquo;) and tick featured products first &mdash; Claude uses them.</p>

        <Box label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject" placeholder="e.g. Weekend special — 15% off select peptides" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
        <Box label="Message"><Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} data-testid="campaign-message" placeholder="Write your announcement…" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>

        <div className="mt-2">
          <label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 flex items-center gap-2"><Sparkles size={13} className="text-alp-gold" /> Feature products (optional)</label>
          <div className="max-h-44 overflow-y-auto border border-alp-line rounded p-2 grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="campaign-products">
            {products.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-alp-cream/85 px-2 py-1 rounded hover:bg-alp-dark/50 cursor-pointer">
                <input type="checkbox" checked={featured.includes(p.id)} onChange={() => toggleFeatured(p.id)} data-testid={`campaign-product-${p.id}`} className="accent-alp-gold" />
                {p.name} <span className="text-alp-gray text-xs">{p.size}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 mt-5 border-t border-alp-line/60 pt-5">
          <div className="flex-1 min-w-[200px]">
            <Box label="Send a test to yourself first"><Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="campaign-test-email" placeholder="you@example.com" className="bg-alp-dark border-alp-line text-alp-cream" /></Box>
          </div>
          <button onClick={() => send(true)} disabled={sending} data-testid="campaign-send-test" className="inline-flex items-center gap-2 border border-alp-gold/50 text-alp-gold px-5 py-2.5 text-xs tracking-[0.15em] uppercase hover:bg-alp-gold hover:text-alp-black transition-colors rounded disabled:opacity-50"><Send size={14} /> Send test</button>
          <button onClick={() => send(false)} disabled={sending} data-testid="campaign-send" className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-50"><Send size={14} /> {sending ? 'Sending…' : `Send to ${audienceCount}`}</button>
        </div>
      </div>

      {/* Registered members */}
      <div className="border border-alp-line rounded-lg p-6 bg-alp-card/40" data-testid="members-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h3 className="font-serif text-xl text-alp-cream flex items-center gap-2"><Users size={18} /> Registered Members</h3>
          {selectedEmails.length > 0 && (
            <button onClick={() => setAudience('members')} data-testid="members-use-selected" className="inline-flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase border border-alp-gold/50 text-alp-gold px-3 py-1.5 rounded hover:bg-alp-gold hover:text-alp-black transition-colors">
              <CheckCircle2 size={13} /> Email {selectedEmails.length} selected
            </button>
          )}
        </div>
        <p className="text-alp-gray text-sm mb-4">Everyone who created an account at the gate or on the account page. Tick members to hand-pick a marketing send.</p>
        <Input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search name or email…" data-testid="members-search" className="bg-alp-dark border-alp-line text-alp-cream mb-4 max-w-sm" />
        {filteredMembers.length === 0 ? (
          <p className="text-alp-gray text-sm py-8 text-center" data-testid="members-empty">{members.length === 0 ? 'No registered members yet — they appear here as people sign up at the gate.' : 'No members match your search.'}</p>
        ) : (
          <div className="border border-alp-line rounded overflow-x-auto">
            <table className="w-full text-sm" data-testid="members-table">
              <thead>
                <tr className="border-b border-alp-line text-left text-[10px] tracking-[0.15em] uppercase text-alp-gray">
                  <th className="px-3 py-2.5 w-8"><input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} data-testid="members-select-all" className="accent-alp-gold" /></th>
                  <th className="px-3 py-2.5">Name</th>
                  <th className="px-3 py-2.5">Email</th>
                  <th className="px-3 py-2.5">Joined</th>
                  <th className="px-3 py-2.5 text-right">Orders</th>
                  <th className="px-3 py-2.5 text-right">Spent (paid)</th>
                  <th className="px-3 py-2.5 text-right">Credit</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((m) => (
                  <tr key={m.id} data-testid={`member-row-${m.id}`} className={`border-b border-alp-line/50 last:border-0 transition-colors ${selected[m.email] ? 'bg-alp-gold/[0.07]' : 'hover:bg-alp-dark/40'}`}>
                    <td className="px-3 py-2.5"><input type="checkbox" checked={!!selected[m.email]} onChange={() => toggleMember(m.email)} data-testid={`member-select-${m.id}`} className="accent-alp-gold" /></td>
                    <td className="px-3 py-2.5 text-alp-cream">{m.fullName || '\u2014'}</td>
                    <td className="px-3 py-2.5 text-alp-cream/75">{m.email}</td>
                    <td className="px-3 py-2.5 text-alp-gray">{fmtDate(m.createdAt)}</td>
                    <td className="px-3 py-2.5 text-right text-alp-cream">{m.orderCount}</td>
                    <td className="px-3 py-2.5 text-right text-alp-gold">${Number(m.totalSpent || 0).toFixed(2)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-300" data-testid={`member-credit-${m.id}`}>${Number(m.credit || 0).toFixed(2)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => adjustCredit(m)} data-testid={`member-add-credit-${m.id}`} title="Add / remove store credit" className="text-[10px] tracking-[0.1em] uppercase border border-emerald-400/40 text-emerald-300 rounded px-2 py-1 mr-1.5 hover:bg-emerald-400/10 transition-colors">&plusmn; Credit</button>
                      <button onClick={() => copyResetLink(m)} data-testid={`member-reset-link-${m.id}`} title="Copy a manual password reset link" className="text-[10px] tracking-[0.1em] uppercase border border-alp-gold/50 text-alp-gold rounded px-2 py-1 mr-1.5 hover:bg-alp-gold hover:text-alp-black transition-colors">Reset link</button>
                      <button onClick={() => setMemberPassword(m)} data-testid={`member-set-password-${m.id}`} title="Set a new password directly" className="text-[10px] tracking-[0.1em] uppercase border border-alp-line text-alp-cream/70 rounded px-2 py-1 mr-1.5 hover:border-alp-gold/50 hover:text-alp-gold transition-colors">Set PW</button>
                      <button onClick={() => editMember(m)} data-testid={`member-edit-${m.id}`} title="Edit name / email" className="text-[10px] tracking-[0.1em] uppercase border border-alp-line text-alp-cream/70 rounded px-2 py-1 mr-1.5 hover:border-alp-gold/50 hover:text-alp-gold transition-colors">Edit</button>
                      <button onClick={() => deleteMember(m)} data-testid={`member-delete-${m.id}`} title="Delete account" className="text-[10px] tracking-[0.1em] uppercase border border-red-500/40 text-red-400 rounded px-2 py-1 hover:bg-red-500/10 transition-colors">Del</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Marketing send history */}
      <div className="border border-alp-line rounded-lg p-6 bg-alp-card/40" data-testid="campaign-history-panel">
        <h3 className="font-serif text-xl text-alp-cream mb-1 flex items-center gap-2"><History size={18} /> Marketing Emails Sent</h3>
        <p className="text-alp-gray text-sm mb-4">Every campaign blast, newest first. Full copies also live under Settings &rarr; Emails.</p>
        {history.length === 0 ? (
          <p className="text-alp-gray text-sm py-6 text-center" data-testid="campaign-history-empty">No marketing emails sent yet.</p>
        ) : (
          <div className="space-y-2">
            {history.slice(0, 25).map((h) => (
              <div key={h.id} data-testid={`campaign-history-${h.id}`} className="flex flex-wrap items-center justify-between gap-2 border border-alp-line/60 rounded px-4 py-3 bg-alp-dark/30">
                <div className="min-w-0">
                  <p className="text-alp-cream text-sm truncate">{h.subject}</p>
                  <p className="text-alp-gray text-[11px] mt-0.5">{h.to} &middot; {h.info || ''}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded border ${h.status === 'sent' ? 'text-emerald-300 border-emerald-400/40' : 'text-amber-300 border-amber-500/40'}`}>{h.status}</span>
                  <p className="text-alp-gray text-[11px] mt-1">{h.createdAt ? new Date(h.createdAt).toLocaleString() : ''}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
